import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
  Image,
  Modal,
  ActivityIndicator,
  useWindowDimensions,
  Linking,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Toast from 'react-native-toast-message';
import RNFS from 'react-native-fs';
import QrCodeView from './QrCodeView';
import { getAllKidsCreator } from '../slices/authSlice';
import { TicketIcon, ChevronRightIcon, CalendarIcon, ClockIcon, DownloadIcon } from './EventIcons';
import API_PATHS from '../apiCalls/ApiPath';

const WIDE_BREAKPOINT = 720;

// Matches the app-wide green; GREEN_INK is the darker shade for green text and
// stroked icons on light grounds, where #A6CE39 is too pale to read.
const GREEN = '#A6CE39';
const GREEN_INK = '#5F8F22';
const INK = '#171720';
const MUTED = '#8B8B96';
const HAIRLINE = '#EBEBF2';
const CHIP_BG = '#EEF0FC';
const CHIP_BORDER = '#E1E4F8';

const FONT = Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans';
const FONT_BOLD = Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans';

const cardShadow = Platform.select({
  web: { boxShadow: '0 20px 50px rgba(15,18,38,0.25)' },
  default: {
    shadowColor: '#0F1226',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 10,
  },
});

const formatTicketType = (type) => {
  if (!type) return '';
  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatDateLong = (epochSeconds) => {
  if (!epochSeconds) return '—';
  const date = new Date(epochSeconds * 1000);
  return date.toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AddParticipantModal = ({
  visible,
  onClose,
  tickets = [],
  eventId,
  eventTitle,
  eventDate,
  editRegistration = null,
}) => {
  const dispatch = useDispatch();
  const getAllkids = useSelector((state) => state.auth.getAllkids);
  const kids = getAllkids?.Data?.kids || [];
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;
  const isEditMode = !!editRegistration;

  const [step, setStep] = useState('ticket');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedKids, setSelectedKids] = useState([]);
  const [kidsRequested, setKidsRequested] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [confirmedRegistrations, setConfirmedRegistrations] = useState([]);
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const qrRefs = useRef({}); // { [registrationId]: QrCodeView node }
  const bookingKeySeq = useRef(0); // stable client keys for pending rows

  useEffect(() => {
    if (visible && step === 'kids' && !kidsRequested) {
      dispatch(getAllKidsCreator());
      setKidsRequested(true);
    }
  }, [visible, step, kidsRequested, dispatch]);

  // In edit mode the modal opens straight on the review step.
  useEffect(() => {
    if (visible && isEditMode) {
      setStep((s) => (s === 'ticket' ? 'review' : s));
    }
  }, [visible, isEditMode]);

  // Existing registration tickets carry ticketId/registrationTicketId but not
  // eventDateTicketId — resolve it against this date's ticket catalog.
  const resolveEventDateTicketId = (t) => {
    if (t.eventDateTicketId != null) return t.eventDateTicketId;
    const byId = tickets.find((dt) => dt.ticketId != null && dt.ticketId === t.ticketId);
    if (byId) return byId.eventDateTicketId;
    const byTitleType = tickets.find((dt) => dt.title === t.title && dt.ticketType === t.ticketType);
    if (byTitleType) return byTitleType.eventDateTicketId;
    return tickets.find((dt) => dt.title === t.title)?.eventDateTicketId ?? null;
  };

  const resetState = () => {
    setStep('ticket');
    setSelectedTicket(null);
    setSelectedKids([]);
    setKidsRequested(false);
    setConfirming(false);
    setPendingBookings([]);
    setConfirmedRegistrations([]);
    setConfirmedTotal(0);
    qrRefs.current = {};
    bookingKeySeq.current = 0;
  };

  const anyQrLoading = confirmedRegistrations.some((r) => r.qrLoading);

  const handleClose = () => {
    if (anyQrLoading) return;
    const bookingCompleted = step === 'confirmed';
    resetState();
    onClose(bookingCompleted);
  };

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedKids([]);
  };

  const handleNext = () => {
    if (!selectedTicket) return;
    setStep('kids');
  };

  const handleBack = () => {
    if (step === 'kids') {
      setStep('ticket');
      return;
    }
    if (step === 'ticket') {
      setStep('review');
    }
  };

  const handleKidToggle = (kid) => {
    const isSelected = selectedKids.some((k) => k.id === kid.id);
    if (isSelected) {
      setSelectedKids((prev) => prev.filter((k) => k.id !== kid.id));
      return;
    }
    if (selectedKids.length >= selectedTicket.no_of_kids) return;
    setSelectedKids((prev) => [...prev, kid]);
  };

  const handleAddBooking = () => {
    if (!selectedTicket || selectedKids.length !== (selectedTicket.no_of_kids || 0)) return;
    const key = `pb_${bookingKeySeq.current++}`;
    setPendingBookings((prev) => [
      ...prev,
      { key, eventDateTicketId: selectedTicket.eventDateTicketId, ticket: selectedTicket, kids: selectedKids },
    ]);
    setSelectedTicket(null);
    setSelectedKids([]);
    setStep('review');
  };

  const handleRemovePending = (key) => {
    setPendingBookings((prev) => {
      const next = prev.filter((b) => b.key !== key);
      if (next.length === 0) setStep('ticket');
      return next;
    });
  };

  const handleAddAnotherTicket = () => {
    setSelectedTicket(null);
    setSelectedKids([]);
    setStep('ticket');
  };

  const resolveKidNames = (kidIds = []) => {
    const map = {};
    const add = (k) => {
      if (!k) return;
      if (k.kidId) map[String(k.kidId)] = k.name;
      if (k.id != null) map[String(k.id)] = k.name;
    };
    kids.forEach(add);
    pendingBookings.forEach((b) => b.kids.forEach(add));
    return kidIds.map((id) => map[String(id)] || id).join(', ');
  };

  const buildConfirmedRegistrations = (registrations) =>
    registrations
      .filter((reg) => reg && reg.registrationId)
      .map((reg) => ({
        registrationId: reg.registrationId,
        invoiceId: reg.invoiceId || null,
        totalAmount: Number(reg.totalAmount) || 0,
        tickets: (reg.tickets || []).map((t) => ({
          registrationTicketId: t.registrationTicketId,
          ticketCode: t.ticketCode,
          title: t.title || 'Ticket',
          ticketPrice: Number(t.ticketPrice) || 0,
          kidNames: resolveKidNames(t.kidIds),
        })),
        qrToken: null,
        qrLoading: true,
        error: null,
      }));

  const patchRegistration = (registrationId, patch) =>
    setConfirmedRegistrations((prev) =>
      prev.map((r) => (r.registrationId === registrationId ? { ...r, ...patch } : r)),
    );

  const generateRegistrationQr = async (registrationId) => {
    if (!registrationId) return;
    patchRegistration(registrationId, { qrLoading: true, error: null, qrToken: null });
    try {
      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.GENERATE_EVENT_TICKET_QR}`, {
        registrationId,
      });
      if (response.data?.Success === 1 || response.data?.Success === true) {
        const qrToken = response.data?.Data?.qrToken || null;
        patchRegistration(registrationId, {
          qrToken,
          qrLoading: false,
          error: qrToken ? null : 'Could not generate the QR code.',
        });
      } else {
        patchRegistration(registrationId, {
          qrLoading: false,
          error: response.data?.Message || 'Could not generate the QR code.',
        });
      }
    } catch (error) {
      console.error('Error generating event ticket QR:', error);
      patchRegistration(registrationId, {
        qrLoading: false,
        error: error?.response?.data?.Message || 'Could not generate the QR code.',
      });
    }
  };

  const runQrBatch = (regs) => {
    Promise.allSettled(
      regs.map((r) => r.registrationId).filter(Boolean).map((id) => generateRegistrationQr(id)),
    );
  };

  const handleConfirm = async () => {
    if (!pendingBookings.length || confirming) return;
    setConfirming(true);
    try {
      if (isEditMode) {
        // Only the newly added tickets are sent; existing ones stay untouched server-side.
        const editResponse = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.EDIT_EVENT_REGISTRATION}`, {
          registrationId: editRegistration.registrationId,
          bookings: pendingBookings.map((b) => ({
            eventDateTicketId: b.eventDateTicketId,
            kidIds: b.kids.map((k) => k.kidId ?? k.id),
          })),
        });
        if (editResponse.data?.Success === 1 || editResponse.data?.Success === true) {
          Toast.show({ type: 'success', text1: editResponse.data?.Message || 'Registration updated successfully!' });
          resetState();
          onClose(true);
        } else {
          Toast.show({
            type: 'error',
            text1: editResponse.data?.Message || 'Could not update your registration. Please try again.',
          });
        }
        return;
      }

      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.REGISTER_FOR_EVENT}`, {
        eventId,
        bookings: pendingBookings.map((b) => ({
          eventDateTicketId: b.eventDateTicketId,
          kidIds: b.kids.map((k) => k.kidId ?? k.id),
        })),
      });

      if (response.data?.Success === 1 || response.data?.Success === true) {
        Toast.show({ type: 'success', text1: response.data?.Message || 'Registration confirmed successfully!' });
        const registrations = response.data?.Data?.registrations || [];
        const built = buildConfirmedRegistrations(registrations);
        const regTotal = built.reduce((s, r) => s + r.totalAmount, 0);
        setConfirmedTotal(
          regTotal || pendingBookings.reduce((s, b) => s + (Number(b.ticket?.price) || 0), 0),
        );
        setConfirmedRegistrations(built);
        setStep('confirmed');
        runQrBatch(built);
      } else {
        Toast.show({ type: 'error', text1: response.data?.Message || 'Could not complete your registration. Please try again.' });
      }
    } catch (error) {
      console.error('Error registering for event:', error);
      Toast.show({
        type: 'error',
        text1: error?.response?.data?.Message || 'Something went wrong. Please try again.',
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleDownloadQr = (registrationId) => {
    const node = qrRefs.current[registrationId];
    if (!node?.toDataURL) return;
    node.toDataURL(async (base64) => {
      const filename = `booking-qr-${registrationId}.png`;
      if (Platform.OS === 'web') {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${base64}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        try {
          const path = `${RNFS.DocumentDirectoryPath}/${filename}`;
          await RNFS.writeFile(path, base64, 'base64');
          Toast.show({ type: 'success', text1: 'QR code downloaded.' });
        } catch (error) {
          console.error('Error saving QR code:', error);
          Toast.show({ type: 'error', text1: 'Could not download QR code.' });
        }
      }
    });
  };

  const payableInvoiceIds = [...new Set(confirmedRegistrations.map((r) => r.invoiceId).filter(Boolean))];
  if (payableInvoiceIds.length > 1) {
    console.warn('Multiple registrations/invoices returned for one booking; using the first for combined Pay.');
  }
  const payableInvoiceId = payableInvoiceIds[0] || null;

  const handlePayNow = () => {
    if (!payableInvoiceId) return;
    const url = `https://freedommerchants.com/danileeshhmulti.php?inum=${encodeURIComponent(payableInvoiceId)}`;
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
      return;
    }
    Linking.openURL(url).catch((error) => {
      console.error('Error opening payment link:', error);
      Toast.show({ type: 'error', text1: 'Could not open the payment page.' });
    });
  };

  const kidsLoading = kidsRequested && !getAllkids?.Data;
  const selectionComplete = selectedTicket && selectedKids.length === selectedTicket.no_of_kids;
  const allocatedByTicket = pendingBookings.reduce((acc, b) => {
    acc[b.eventDateTicketId] = (acc[b.eventDateTicketId] || 0) + b.kids.length;
    return acc;
  }, {});
  const usedKidIdsForSelectedTicket = new Set(
    selectedTicket
      ? [
          ...pendingBookings
            .filter((b) => b.eventDateTicketId === selectedTicket.eventDateTicketId)
            .flatMap((b) => b.kids.flatMap((k) => [k.id, k.kidId])),
          ...(isEditMode
            ? (editRegistration.tickets || [])
                .filter((t) => resolveEventDateTicketId(t) === selectedTicket.eventDateTicketId)
                .flatMap((t) => (t.kids || []).map((k) => k.kidId))
            : []),
        ].filter((v) => v != null)
      : [],
  );

  return (
    <Modal animationType="fade" transparent visible={visible} onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <View style={[styles.card, cardShadow, step === 'confirmed' && isWide && styles.cardWide]}>
          {step !== 'confirmed' && (
            <View style={styles.headerRow}>
              {step === 'kids' ||
              (step === 'ticket' && (pendingBookings.length > 0 || isEditMode)) ? (
                <TouchableOpacity style={styles.iconButton} onPress={handleBack}>
                  <View style={styles.chevronBack}>
                    <ChevronRightIcon size={18} color={INK} strokeWidth={2.4} />
                  </View>
                </TouchableOpacity>
              ) : (
                <View style={styles.iconButton} />
              )}

              <View style={styles.headerTextWrap}>
                <Text style={styles.headerTitle}>
                  {step === 'ticket'
                    ? 'Select Ticket'
                    : step === 'review'
                      ? isEditMode
                        ? 'Edit Registration'
                        : 'Review Registration'
                      : 'Select Kids'}
                </Text>
                {step === 'kids' && selectedTicket && (
                  <Text style={styles.headerSubtitle}>
                    Choose {selectedTicket.no_of_kids} kid{selectedTicket.no_of_kids > 1 ? 's' : ''} for{' '}
                    {selectedTicket.title}
                  </Text>
                )}
              </View>

              <TouchableOpacity style={styles.iconButton} onPress={handleClose}>
                <View style={styles.closeChip}>
                  <Text style={styles.closeChipText}>×</Text>
                </View>
              </TouchableOpacity>
            </View>
          )}

          {step === 'ticket' ? (
            <>
              <ScrollView style={styles.body} contentContainerStyle={styles.ticketList}>
                {tickets.length === 0 ? (
                  <Text style={styles.emptyText}>No tickets available for this date.</Text>
                ) : (
                  tickets.map((ticket) => {
                    const isSelected = selectedTicket?.eventDateTicketId === ticket.eventDateTicketId;
                    const effectiveRemaining =
                      (ticket.remainingSeats ?? 0) - (allocatedByTicket[ticket.eventDateTicketId] || 0);
                    const soldOut = effectiveRemaining < (ticket.no_of_kids || 1);
                    return (
                      <TouchableOpacity
                        key={ticket.eventDateTicketId}
                        activeOpacity={0.85}
                        disabled={soldOut}
                        style={[
                          styles.ticketRow,
                          isSelected && styles.ticketRowSelected,
                          soldOut && styles.ticketRowDisabled,
                        ]}
                        onPress={() => handleTicketSelect(ticket)}
                      >
                        <View style={[styles.ticketIconChip, isSelected && styles.ticketIconChipSelected]}>
                          <TicketIcon size={20} color={isSelected ? '#fff' : GREEN_INK} strokeWidth={2} />
                        </View>

                        <View style={{ flex: 1 }}>
                          <Text style={styles.ticketTitle}>{ticket.title}</Text>
                          <View style={styles.ticketBadgeRow}>
                            <View style={styles.dayTypeBadge}>
                              <Text style={styles.dayTypeBadgeText}>{formatTicketType(ticket.ticketType)}</Text>
                            </View>
                            <Text style={styles.ticketPriceText}>${ticket.price}</Text>
                          </View>
                          <Text style={styles.ticketSeatsText}>
                            {soldOut
                              ? 'Sold out'
                              : `${Math.max(effectiveRemaining, 0)}/${ticket.totalSeats} seats left`}
                          </Text>
                        </View>

                        <View style={[styles.radioOuter, isSelected && styles.radioOuterSelected]}>
                          {isSelected && <View style={styles.radioInner} />}
                        </View>
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>

              <View style={styles.footer}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  disabled={!selectedTicket}
                  style={[styles.primaryButton, !selectedTicket && styles.primaryButtonDisabled]}
                  onPress={handleNext}
                >
                  <Text style={styles.primaryButtonText}>Next</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : step === 'kids' ? (
            <>
              <View style={styles.selectionPill}>
                <Text style={styles.selectionPillText}>
                  {selectedKids.length}/{selectedTicket?.no_of_kids} selected
                </Text>
              </View>

              <ScrollView style={styles.body} contentContainerStyle={styles.kidsList}>
                {kidsLoading ? (
                  <View style={styles.loadingBox}>
                    <ActivityIndicator size="small" color={GREEN_INK} />
                  </View>
                ) : kids.length === 0 ? (
                  <Text style={styles.emptyText}>No kids found on your profile.</Text>
                ) : (
                  kids.map((kid) => {
                    const isSelected = selectedKids.some((k) => k.id === kid.id);
                    const alreadyStaged =
                      usedKidIdsForSelectedTicket.has(kid.id) || usedKidIdsForSelectedTicket.has(kid.kidId);
                    const disabled =
                      alreadyStaged ||
                      (!isSelected && selectedKids.length >= (selectedTicket?.no_of_kids || 0));
                    return (
                      <TouchableOpacity
                        key={kid.id}
                        activeOpacity={0.85}
                        disabled={disabled}
                        style={[
                          styles.kidRow,
                          isSelected && styles.kidRowSelected,
                          disabled && styles.kidRowDisabled,
                        ]}
                        onPress={() => handleKidToggle(kid)}
                      >
                        <Image
                          source={kid.profilePic ? { uri: kid.profilePic } : require('../assets/AddKidPic.png')}
                          style={styles.kidImage}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.kidName}>{kid.name}</Text>
                          <Text style={styles.kidMeta}>
                            {alreadyStaged
                              ? 'Already added to this ticket'
                              : `${kid.age} years, ${kid.gender === 'BOY' ? 'Boy' : 'Girl'}`}
                          </Text>
                        </View>
                        <View style={[styles.checkCircle, isSelected && styles.checkCircleSelected]}>
                          {isSelected && <Text style={styles.checkMark}>✓</Text>}
                        </View>
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>

              <View style={styles.footer}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  disabled={!selectionComplete}
                  style={[styles.primaryButton, !selectionComplete && styles.primaryButtonDisabled]}
                  onPress={handleAddBooking}
                >
                  <Text style={styles.primaryButtonText}>Add ticket</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : step === 'review' ? (
            <>
              <ScrollView style={styles.body} contentContainerStyle={styles.reviewList}>
                {isEditMode && (
                  <>
                    <Text style={styles.reviewSectionLabel}>EXISTING TICKETS</Text>
                    {(editRegistration.tickets || []).map((t) => (
                      <View key={t.registrationTicketId} style={[styles.reviewRow, styles.reviewRowLocked]}>
                        <View style={styles.ticketIconChip}>
                          <TicketIcon size={18} color={MUTED} strokeWidth={2} />
                        </View>
                        <View style={styles.reviewRowInfo}>
                          <Text style={styles.reviewTicketTitle}>{t.title}</Text>
                          <Text style={styles.reviewKidNames}>
                            {(t.kids || []).map((k) => k.name).join(', ')}
                          </Text>
                        </View>
                        <Text style={styles.reviewPrice}>${t.ticketPrice}</Text>
                      </View>
                    ))}
                    <Text style={styles.reviewSectionLabel}>NEW TICKETS</Text>
                  </>
                )}
                {pendingBookings.length === 0 ? (
                  <Text style={styles.emptyText}>
                    {isEditMode ? "Tap '+ Add another ticket' to add more." : 'No tickets added yet.'}
                  </Text>
                ) : (
                  pendingBookings.map((b) => (
                    <View key={b.key} style={styles.reviewRow}>
                      <View style={styles.ticketIconChip}>
                        <TicketIcon size={18} color={GREEN_INK} strokeWidth={2} />
                      </View>
                      <View style={styles.reviewRowInfo}>
                        <Text style={styles.reviewTicketTitle}>{b.ticket?.title}</Text>
                        <Text style={styles.reviewKidNames}>{b.kids.map((k) => k.name).join(', ')}</Text>
                      </View>
                      <Text style={styles.reviewPrice}>${b.ticket?.price}</Text>
                      <TouchableOpacity
                        activeOpacity={0.8}
                        style={styles.reviewRemoveBtn}
                        onPress={() => handleRemovePending(b.key)}
                      >
                        <View style={styles.closeChip}>
                          <Text style={styles.closeChipText}>×</Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  ))
                )}
              </ScrollView>

              <View style={styles.footer}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  style={styles.secondaryButton}
                  onPress={handleAddAnotherTicket}
                >
                  <Text style={styles.secondaryButtonText}>+ Add another ticket</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  activeOpacity={0.85}
                  disabled={!pendingBookings.length || confirming}
                  style={[
                    styles.primaryButton,
                    styles.reviewConfirmButton,
                    (!pendingBookings.length || confirming) && styles.primaryButtonDisabled,
                  ]}
                  onPress={handleConfirm}
                >
                  {confirming ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={styles.primaryButtonText}>Confirm</Text>
                  )}
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.confirmedCloseButton, anyQrLoading && styles.confirmedCloseButtonDisabled]}
                onPress={handleClose}
                disabled={anyQrLoading}
              >
                <View style={styles.closeChip}>
                  <Text style={styles.closeChipText}>×</Text>
                </View>
              </TouchableOpacity>

              <ScrollView style={styles.confirmedScroll} contentContainerStyle={styles.confirmedScrollContent}>
                <Text style={styles.confirmedTitle}>Registration Confirmed</Text>


                <View style={styles.confirmedHeader}>
                  <Text style={styles.confirmedEventTitle}>{eventTitle || 'Event'}</Text>
                  <View style={styles.confirmedInfoRow}>
                    <CalendarIcon size={18} color={GREEN_INK} strokeWidth={2} />
                    <Text style={styles.confirmedInfoText}>{formatDateLong(eventDate?.eventDate)}</Text>
                  </View>
                  <View style={styles.confirmedInfoRow}>
                    <ClockIcon size={18} color={GREEN_INK} strokeWidth={2} />
                    <Text style={styles.confirmedInfoText}>
                      {eventDate?.startTime} - {eventDate?.endTime}
                    </Text>
                  </View>
                </View>

                <View style={styles.confirmedCardsList}>
                  {confirmedRegistrations.length === 0 && (
                    <Text style={styles.emptyText}>
                      Registration was created, but no details were returned. Check the Registrations list.
                    </Text>
                  )}
                  {confirmedRegistrations.map((reg, index) => (
                    <View key={`${reg.registrationId || 'na'}-${index}`} style={styles.confirmedCard}>
                      <View style={styles.qrBox}>
                        {reg.qrToken ? (
                          <QrCodeView
                            ref={(n) => {
                              if (reg.registrationId) qrRefs.current[reg.registrationId] = n;
                            }}
                            value={reg.qrToken}
                            size={188}
                          />
                        ) : reg.qrLoading ? (
                          <ActivityIndicator size="large" color={GREEN_INK} />
                        ) : (
                          <View style={styles.qrErrorBox}>
                            {!!reg.error && <Text style={styles.qrErrorText}>{reg.error}</Text>}
                            {!!reg.registrationId && (
                              <TouchableOpacity
                                activeOpacity={0.8}
                                style={styles.qrRetryButton}
                                onPress={() => generateRegistrationQr(reg.registrationId)}
                              >
                                <Text style={styles.qrRetryText}>Retry</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        )}
                      </View>

                      <TouchableOpacity
                        activeOpacity={0.8}
                        style={[styles.downloadButton, !reg.qrToken && styles.downloadButtonDisabled]}
                        onPress={() => handleDownloadQr(reg.registrationId)}
                        disabled={!reg.qrToken}
                      >
                        <DownloadIcon size={18} color={GREEN_INK} strokeWidth={2} />
                        <Text style={styles.downloadButtonText}>Download QR</Text>
                      </TouchableOpacity>

                      <View style={styles.confirmedTicketLines}>
                        {reg.tickets.map((t) => (
                          <View key={t.registrationTicketId} style={styles.confirmedTicketLine}>
                            <View style={styles.confirmedTicketLineInfo}>
                              <Text style={styles.confirmedCardTitle}>{t.title}</Text>
                              {!!t.kidNames && <Text style={styles.confirmedCardKids}>{t.kidNames}</Text>}
                            </View>
                            <Text style={styles.confirmedKidPrice}>${t.ticketPrice}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>

                <View style={styles.confirmedTotalRow}>
                  <Text style={styles.confirmedTotalLabel}>Total</Text>
                  <Text style={styles.confirmedTotalValue}>${confirmedTotal}</Text>
                </View>

                <TouchableOpacity
                  activeOpacity={0.85}
                  disabled={!payableInvoiceId}
                  style={[styles.primaryButton, styles.payNowButton, !payableInvoiceId && styles.primaryButtonDisabled]}
                  onPress={handlePayNow}
                >
                  <Text style={styles.primaryButtonText}>Pay Now</Text>
                </TouchableOpacity>
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15,18,38,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    backgroundColor: '#fff',
    borderRadius: 22,
    paddingTop: 20,
    paddingBottom: 24,
    paddingHorizontal: 22,
  },
  cardWide: {
    maxWidth: 760,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  iconButton: {
    width: 32,
    alignItems: 'flex-start',
  },
  chevronBack: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ scaleX: -1 }],
  },
  closeChip: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F6FD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeChipText: {
    fontSize: 20,
    lineHeight: 20,
    color: MUTED,
    fontFamily: FONT_BOLD,
  },
  headerTextWrap: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  headerTitle: {
    fontSize: 19,
    color: INK,
    fontFamily: FONT_BOLD,
    letterSpacing: -0.2,
  },
  headerSubtitle: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 4,
    textAlign: 'center',
  },
  body: {
    maxHeight: 360,
    flexShrink: 1,
  },
  ticketList: {
    gap: 12,
  },
  ticketRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    padding: 14,
    marginBottom: 12,
    backgroundColor: '#fff',
  },
  ticketRowSelected: {
    borderColor: GREEN,
    backgroundColor: '#F3F9EC',
  },
  ticketRowDisabled: {
    opacity: 0.45,
  },
  ticketIconChip: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ticketIconChipSelected: {
    backgroundColor: GREEN,
    borderColor: GREEN,
  },
  ticketTitle: {
    fontSize: 16,
    color: INK,
    fontFamily: FONT_BOLD,
    marginBottom: 6,
  },
  dayTypeBadge: {
    alignSelf: 'flex-start',
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  dayTypeBadgeText: {
    fontSize: 10.5,
    letterSpacing: 0.5,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
  },
  ticketBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ticketPriceText: {
    fontSize: 13,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  ticketSeatsText: {
    fontSize: 12,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 5,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: CHIP_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: GREEN,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: GREEN,
  },
  selectionPill: {
    alignSelf: 'center',
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 14,
  },
  selectionPillText: {
    fontSize: 12.5,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
    letterSpacing: 0.3,
  },
  kidsList: {
    paddingBottom: 4,
  },
  loadingBox: {
    paddingVertical: 30,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: MUTED,
    fontSize: 14,
    fontFamily: FONT,
    paddingVertical: 30,
  },
  kidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    padding: 12,
    marginBottom: 10,
    backgroundColor: '#fff',
  },
  kidRowSelected: {
    borderColor: GREEN,
    backgroundColor: '#F3F9EC',
  },
  kidRowDisabled: {
    opacity: 0.45,
  },
  kidImage: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: CHIP_BG,
  },
  kidName: {
    fontSize: 15.5,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  kidMeta: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 3,
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: CHIP_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkCircleSelected: {
    borderColor: GREEN,
    backgroundColor: GREEN,
  },
  checkMark: {
    color: '#fff',
    fontSize: 13,
    fontFamily: FONT_BOLD,
  },
  footer: {
    marginTop: 18,
  },
  primaryButton: {
    backgroundColor: GREEN,
    borderRadius: 12,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: '#C9DEB0',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: FONT_BOLD,
  },
  confirmedCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
  },
  confirmedCloseButtonDisabled: {
    opacity: 0.35,
  },
  confirmedScroll: {
    flexShrink: 1,
  },
  confirmedScrollContent: {
    paddingTop: 8,
  },
  confirmedLayout: {
    flexDirection: 'column',
  },
  confirmedLayoutWide: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  confirmedQrColumn: {
    alignItems: 'center',
    paddingTop: 8,
  },
  confirmedQrColumnWide: {
    flex: 1,
    paddingRight: 28,
  },
  confirmedTitle: {
    fontSize: 21,
    color: INK,
    fontFamily: FONT_BOLD,
    letterSpacing: -0.2,
  },
  confirmedSubtitle: {
    fontSize: 13.5,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 6,
    textAlign: 'center',
  },
  qrBox: {
    width: '100%',
    maxWidth: 220,
    aspectRatio: 1,
    marginTop: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    backgroundColor: '#F9FAFE',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  qrRetryButton: {
    borderWidth: 1,
    borderColor: GREEN,
    borderRadius: 10,
    paddingHorizontal: 22,
    paddingVertical: 10,
  },
  qrRetryText: {
    fontSize: 14,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 18,
    width: '100%',
    maxWidth: 220,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 12,
    paddingVertical: 13,
  },
  downloadButtonDisabled: {
    opacity: 0.4,
  },
  downloadButtonText: {
    fontSize: 14.5,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  confirmedDivider: {
    height: 1,
    backgroundColor: HAIRLINE,
    marginVertical: 24,
  },
  confirmedDividerWide: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: HAIRLINE,
    marginHorizontal: 28,
  },
  confirmedDetailsColumn: {
    paddingTop: 8,
  },
  confirmedDetailsColumnWide: {
    flex: 1,
    paddingTop: 44,
  },
  confirmedEventTitle: {
    fontSize: 19,
    color: INK,
    fontFamily: FONT_BOLD,
    marginBottom: 16,
  },
  confirmedInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  confirmedInfoText: {
    fontSize: 14.5,
    color: '#4A4A55',
    fontFamily: FONT,
  },
  confirmedSectionDivider: {
    height: 1,
    backgroundColor: HAIRLINE,
    marginVertical: 16,
  },
  confirmedSectionHeading: {
    fontSize: 11.5,
    letterSpacing: 0.6,
    color: MUTED,
    fontFamily: FONT_BOLD,
    marginBottom: 12,
  },
  confirmedKidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  confirmedKidNames: {
    flex: 1,
    fontSize: 15,
    color: INK,
    fontFamily: FONT,
  },
  confirmedKidPrice: {
    fontSize: 15,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  confirmedTotalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  confirmedTotalLabel: {
    fontSize: 18,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  confirmedTotalValue: {
    fontSize: 18,
    color: GREEN_INK,
    fontFamily: FONT_BOLD,
  },
  payNowButton: {
    marginTop: 22,
  },

  // ---- Review step ----
  reviewList: {
    paddingBottom: 4,
    gap: 12,
  },
  reviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    padding: 14,
    marginBottom: 12,
    backgroundColor: '#fff',
  },
  reviewRowInfo: {
    flex: 1,
  },
  reviewTicketTitle: {
    fontSize: 15,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  reviewKidNames: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 4,
  },
  reviewPrice: {
    fontSize: 14,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  reviewRemoveBtn: {
    marginLeft: 2,
  },
  reviewSectionLabel: {
    fontSize: 10.5,
    letterSpacing: 1,
    color: MUTED,
    fontFamily: FONT_BOLD,
    marginTop: 4,
    marginBottom: 6,
  },
  reviewRowLocked: {
    backgroundColor: '#F7F7FB',
    opacity: 0.9,
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: GREEN,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  secondaryButtonText: {
    color: GREEN_INK,
    fontSize: 15,
    fontFamily: FONT_BOLD,
  },
  reviewConfirmButton: {
    marginTop: 12,
  },

  // ---- Confirmed step (multi-ticket) ----
  confirmedHeader: {
    marginTop: 18,
    marginBottom: 6,
  },
  confirmedCardsList: {
    marginTop: 8,
  },
  confirmedCard: {
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    borderRadius: 16,
    padding: 16,
    marginBottom: 14,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  confirmedTicketLines: {
    alignSelf: 'stretch',
    marginTop: 14,
    borderTopWidth: 1,
    borderTopColor: HAIRLINE,
    paddingTop: 12,
    gap: 12,
  },
  confirmedTicketLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  confirmedTicketLineInfo: {
    flex: 1,
  },
  confirmedCardTitle: {
    fontSize: 15,
    color: INK,
    fontFamily: FONT_BOLD,
  },
  confirmedCardKids: {
    fontSize: 12.5,
    color: MUTED,
    fontFamily: FONT,
    marginTop: 4,
  },
  qrErrorBox: {
    alignItems: 'center',
  },
  qrErrorText: {
    fontSize: 12,
    color: MUTED,
    fontFamily: FONT,
    textAlign: 'center',
    marginBottom: 8,
  },
});

export default AddParticipantModal;
